import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { View } from 'react-native';
import { connect } from 'react-redux';
import { useRoute, useFocusEffect } from '@react-navigation/native';

import ColorWrapper from '@styles/colors';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import JobsListSelector from './JobsListSelector';
import JobContent from './JobContent';
import OperatorPanelInformationJobsActions from './OperatorPanelInformationJobsAction';
import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import JobEmptyScreen from './JobEmptyScreen';
import OperatorPanelLoadingScreen from '@components/operatorPanel/components/OperatorPanelInformation/components/common/OperatorPanelLoadingScreen';

import { operationsSearch } from '@reducers/search';
import { JOB_STATUSES } from '@helpers';
import { scaleOP } from '@components/frame';
import { OperationSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { ColorFunction } from '@styles/colors/types';
import { ReducerEntityWithLoadingState } from '@v2/pages/common/interfaces';
import { SearchEntity } from '@interfaces/methods';
import { scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { fontOP } from '@styles/fonts';
import { useCloseProductionOperationErrorAlert } from '@v2/components/FinishJobModal/hooks/useCloseProductionOperationErrorAlert';
import { useListenForClosedOperations } from '@hooks/useListenForClosedOperations';

interface OperatorPanelInformationJobsScreenProps {
  operationsSearch: SearchEntity<OperationSearchItem>;
  C: ColorFunction;
  batch: ReducerEntityWithLoadingState<ProductionBatchStatistics | null>;
}

// Track active instance globally
let activeInstanceId: string | null = null;

const OperatorPanelInformationJobsScreen: React.FC<OperatorPanelInformationJobsScreenProps> = (props) => {
  const { operationsSearch, C, batch } = props;

  // Generate a unique instance ID to track multiple renders
  const instanceId = useRef(Math.random().toString(36).substr(2, 9)).current;
  const [isActiveInstance, setIsActiveInstance] = useState(false);

  const route = useRoute();

  const jobId = route.params?.[scheduledProductionOperationIdParam];
  const isMachinePerspective = jobId == null;

  // Use a key to force reset state when perspective changes
  const perspectiveKey = isMachinePerspective ? 'machine' : `job-${jobId}`;

  const [jobs, setJobs] = useState<OperationSearchItem[]>([]);
  const [selectedJob, setSelectedJob] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [currentPerspectiveKey, setCurrentPerspectiveKey] = useState<string>(perspectiveKey);

  // Use navigation focus to determine active instance
  useFocusEffect(
    React.useCallback(() => {
      // This screen is focused/visible
      activeInstanceId = instanceId;
      setIsActiveInstance(true);

      // Clear data when screen becomes active to ensure fresh data
      setJobs([]);
      setSelectedJob(undefined);
      setIsLoading(true);

      return () => {
        // This screen is losing focus
        if (activeInstanceId === instanceId) {
          activeInstanceId = null;
        }
        setIsActiveInstance(false);
      };
    }, [instanceId, perspectiveKey, jobId, isMachinePerspective])
  );

  // Guard against stale renders - if perspective doesn't match, don't render old data
  const isStaleRender = currentPerspectiveKey !== perspectiveKey;

  // Track perspective changes
  useEffect(() => {
    if (currentPerspectiveKey !== perspectiveKey) {
      setCurrentPerspectiveKey(perspectiveKey);
    }
  }, [perspectiveKey, instanceId]);

  useEffect(() => {
    if (!isActiveInstance) {
      return;
    }

    if (!batch.wasLoaded && isMachinePerspective) {
      setIsLoading(true);
      return;
    }

    const jobIds = !isMachinePerspective ? [jobId] : [...new Set(batch?.data?.links?.map((l) => l.productionOperationId))];

    if (!jobIds.length) {
      setJobs([]);
      setSelectedJob(undefined);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    setIsLoading(true);

    (async () => {
      try {
        const jobsData = await operationsSearch({
          data: {},
          filter: { oDataFilter: `${jobIds.map((id) => `ProductionOperationId eq ${id}`).join(' or ')}`, orderBy: ['ModifiedOn desc'] },
          select: null,
          resetHistory: true,
          ignoreHistory: true,
          signal: abortController.signal,
        });

        const nextJobs = jobsData?.searchResult ?? [];
        setJobs(nextJobs);
        setSelectedJob(nextJobs[0]?.productionOperationId);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          setJobs([]);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [batch.wasLoaded, batch.data?.links?.length, jobId, isMachinePerspective, isActiveInstance]);

  useListenForClosedOperations({ isActiveInstance, setJobs });

  const markJobFinished = useCallback(
    (productionOperationId: number) => {
      setJobs((prev) =>
        prev.map((j) => (j.productionOperationId === productionOperationId ? { ...j, status: JOB_STATUSES.FINISHED } : j))
      );
    },
    []
  );
  // No onAlreadyFinished — planboard suppresses lastCloseError for JobAlreadyFinished; local state is patched via onJobFinished + useListenForClosedOperations.
  useCloseProductionOperationErrorAlert();

  const selectdJobContent = useMemo(() => {
    // Don't show content if this is a stale render or not active instance
    if (isStaleRender || !isActiveInstance) {
      return undefined;
    }
    const content = jobs.find((job) => job.productionOperationId === selectedJob);
    return content;
  }, [selectedJob, jobs, isStaleRender, instanceId, isActiveInstance]);

  // Show loading if this is a stale render OR if actually loading OR not active instance
  const shouldShowLoading = isStaleRender || isLoading || !isActiveInstance;
  // Only show jobs if not a stale render AND active instance
  const jobsToShow = isStaleRender || !isActiveInstance ? [] : jobs;

  return (
    <>
      <TopPanel title="Jobs">
        <OperatorPanelInformationJobsActions
          selectedJobId={selectedJob}
          showActions={!!selectedJob && isMachinePerspective}
          productionBatchId={batch?.data?.productionBatchId}
          ncFileId={batch?.data?.ncFileId}
          selectedJob={selectdJobContent}
        />
      </TopPanel>
      {shouldShowLoading ? (
        <OperatorPanelLoadingScreen text="Loading jobs" scale={scaleOP} font={fontOP} />
      ) : (
        <View
          testID={TEST_IDS.operatorPanel.information.jobs.root}
          style={[
            {
              backgroundColor: C('Background/Light/Panel [mobile]'),
              display: 'flex',
              flexDirection: 'column',
              gap: scaleOP(24),
              height: '100%',
              width: '100%',
              paddingHorizontal: scaleOP(24),
              flex: 1,
            },
          ]}
        >
          {jobsToShow.length > 0 ? (
            <>
              {isMachinePerspective && <JobsListSelector jobs={jobs} selectedJob={selectedJob} setSelectedJob={setSelectedJob} />}
              <JobContent job={selectdJobContent} onJobFinished={markJobFinished} />
            </>
          ) : (
            <JobEmptyScreen />
          )}
        </View>
      )}
    </>
  );
};

export default connect(({ batch: { batch } }) => ({ batch }), { operationsSearch })(ColorWrapper(OperatorPanelInformationJobsScreen));
